import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Bot, Store, Users, Megaphone, MessagesSquare, ClipboardList, Workflow, BarChart3, Shield, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/bits";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/dashboard/agents", label: "Agents", icon: Bot },
  { to: "/dashboard/retailers", label: "Retailer Network", icon: Store },
  { to: "/dashboard/audiences", label: "Audiences", icon: Users },
  { to: "/dashboard/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/dashboard/conversations", label: "Conversations", icon: MessagesSquare },
  { to: "/dashboard/orders", label: "Orders", icon: ClipboardList },
  { to: "/dashboard/workflows", label: "Workflows & Integrations", icon: Workflow },
  { to: "/dashboard/insights", label: "Insights", icon: BarChart3 },
  { to: "/dashboard/team", label: "Team", icon: Users, admin: true },
  { to: "/dashboard/platform", label: "Platform", icon: Shield, platform: true },
];

export default function DashboardLayout() {
  const { user, company, logout, refreshCompany } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); refreshCompany(); }, [location.pathname]); // eslint-disable-line

  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const isPlatform = Boolean(user?.is_platform_admin);
  const Rail = (
    <aside className="w-[260px] shrink-0 h-full flex flex-col border-r border-white/[0.06] bg-[#0A100D]">
      <div className="px-5 py-5 flex items-center gap-3 border-b border-white/[0.06]">
        <Brand size="lg" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.filter((n) => (!n.admin || isAdmin) && (!n.platform || isPlatform)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <n.icon className="w-4 h-4" /> <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" /> : <div className="w-8 h-8 rounded-full bg-emerald-900" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm text-slate-200 truncate" data-testid="user-name">{user?.name}</p>
              <span className={`shrink-0 px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase ${isAdmin ? "bg-emerald-950/80 border-emerald-500/30 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400"}`} data-testid="user-role-pill">{role}</span>
            </div>
            <p className="text-[11px] text-slate-500 truncate">{company?.name}</p>
          </div>
          <ThemeToggle />
          <button onClick={logout} className="text-slate-500 hover:text-rose-300" data-testid="logout-btn" title="Log out"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex bg-[#080C0A] grain">
      <div className="hidden lg:block h-full">{Rail}</div>
      {open && <div className="fixed inset-0 z-40 lg:hidden flex"><div className="h-full">{Rail}</div><div className="flex-1 bg-black/70" onClick={() => setOpen(false)} /></div>}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#0A100D]">
          <button onClick={() => setOpen(!open)} className="text-slate-300" data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
          <Brand size="sm" />
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-y-auto"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8"><Outlet /></div></main>
      </div>
    </div>
  );
}
